if (!customElements.get('license-type-fields')) {
  customElements.define(
    'license-type-fields',
    class LicenseTypeFields extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.section;
        this.init();
      }

      connectedCallback() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.init());
        } else {
          this.init();
        }
      }

      init() {
        // Find the variant selector for this section
        const variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
        if (!variantSelects) return;

        // Get all option inputs/selects
        this.licenseOptionInputs = this.getLicenseOptionInputs(variantSelects);
        
        if (this.licenseOptionInputs.length === 0) {
          // No license option found, hide the fields container
          this.style.display = 'none';
          return;
        }

        // Show the container
        this.style.display = 'block';

        // Set up event listeners
        this.licenseOptionInputs.forEach(input => {
          if (input.tagName === 'SELECT') {
            input.addEventListener('change', () => this.handleLicenseTypeChange());
          } else if (input.type === 'radio') {
            input.addEventListener('change', () => this.handleLicenseTypeChange());
          }
        });

        // Set up validation listeners
        const checkbox = this.querySelector('#license-confirmation-' + this.sectionId);
        const companyNameInput = this.querySelector('#Company-Name-' + this.sectionId);
        const companyNameField = companyNameInput?.closest('.field');

        if (checkbox) {
          checkbox.addEventListener('change', () => {
            // Clear any error styling when checkbox is checked
            if (checkbox.checked) {
              checkbox.classList.remove('field--error');
            }
          });
        }

        if (companyNameInput) {
          companyNameInput.addEventListener('input', () => {
            // Clear error styling when user starts typing
            if (companyNameField) {
              companyNameField.classList.remove('field--error');
            }
          });
        }

        // Check initial state
        this.handleLicenseTypeChange();
      }

      getLicenseOptionInputs(variantSelects) {
        const inputs = [];
        
        // Check all fieldsets and selects for license-related options
        const allInputs = variantSelects.querySelectorAll('select, input[type="radio"]');
        
        allInputs.forEach(input => {
          // Check if this input is part of a license type option
          const fieldset = input.closest('fieldset');
          const select = input.tagName === 'SELECT' ? input : null;
          
          let optionName = '';
          if (fieldset) {
            const legend = fieldset.querySelector('legend');
            if (legend) {
              optionName = legend.textContent.trim().toLowerCase();
            }
          } else if (select) {
            const label = select.closest('.product-form__input--dropdown')?.querySelector('.form__label');
            if (label) {
              optionName = label.textContent.trim().toLowerCase();
            }
          }

          // Check if this is a license type option
          if (optionName.includes('license') || optionName.includes('licence')) {
            if (select) {
              inputs.push(select);
            } else if (fieldset) {
              // Add all radio inputs from this fieldset
              fieldset.querySelectorAll('input[type="radio"]').forEach(radio => {
                inputs.push(radio);
              });
            }
          }
        });

        return inputs;
      }

      getSelectedLicenseType() {
        let selectedValue = '';

        this.licenseOptionInputs.forEach(input => {
          if (input.tagName === 'SELECT') {
            if (input.value) {
              selectedValue = input.value;
            }
          } else if (input.type === 'radio' && input.checked) {
            selectedValue = input.value;
          }
        });

        return selectedValue.toLowerCase();
      }

      handleLicenseTypeChange() {
        const selectedLicenseType = this.getSelectedLicenseType();
        const checkboxWrapper = this.querySelector('#license-checkbox-wrapper-' + this.sectionId);
        const companyNameWrapper = this.querySelector('#company-name-wrapper-' + this.sectionId);
        const checkbox = this.querySelector('#license-confirmation-' + this.sectionId);
        const companyNameInput = this.querySelector('#Company-Name-' + this.sectionId);

        // Hide both initially
        if (checkboxWrapper) checkboxWrapper.style.display = 'none';
        if (companyNameWrapper) companyNameWrapper.style.display = 'none';
        
        // Reset values and validation
        if (checkbox) {
          checkbox.checked = false;
          checkbox.removeAttribute('required');
        }
        if (companyNameInput) {
          companyNameInput.value = '';
          companyNameInput.removeAttribute('required');
          companyNameInput.classList.remove('field--error');
        }

        // Show appropriate field based on selection
        if (selectedLicenseType.includes('personal')) {
          // Show checkbox for Personal Use
          if (checkboxWrapper) checkboxWrapper.style.display = 'block';
          if (checkbox) {
            checkbox.setAttribute('required', 'required');
          }
        } else if (
          selectedLicenseType.includes('workplace') ||
          selectedLicenseType.includes('non-profit') ||
          selectedLicenseType.includes('nonprofit') ||
          selectedLicenseType.includes('ticketed')
        ) {
          // Show company name field for Workplace/Non-Profit/Ticketed
          if (companyNameWrapper) {
            companyNameWrapper.style.display = 'block';
            if (companyNameInput) {
              companyNameInput.setAttribute('required', 'required');
            }
          }
        }
      }

      validate() {
        const selectedLicenseType = this.getSelectedLicenseType();
        const checkbox = this.querySelector('#license-confirmation-' + this.sectionId);
        const companyNameInput = this.querySelector('#Company-Name-' + this.sectionId);
        const companyNameField = companyNameInput?.closest('.field');

        // If no license type is selected or fields are hidden, validation passes
        if (!selectedLicenseType) {
          return { valid: true };
        }

        if (selectedLicenseType.includes('personal')) {
          if (checkbox && !checkbox.checked) {
            checkbox.focus();
            checkbox.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return {
              valid: false,
              message: 'Please confirm that this event is private and not connected to any business, organisation, or public activity.'
            };
          }
        } else if (
          selectedLicenseType.includes('workplace') ||
          selectedLicenseType.includes('non-profit') ||
          selectedLicenseType.includes('nonprofit') ||
          selectedLicenseType.includes('ticketed')
        ) {
          if (companyNameInput && !companyNameInput.value.trim()) {
            companyNameInput.focus();
            companyNameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            if (companyNameField) {
              companyNameField.classList.add('field--error');
            }
            return {
              valid: false,
              message: 'Please enter your company name.'
            };
          } else if (companyNameField) {
            companyNameField.classList.remove('field--error');
          }
        }

        return { valid: true };
      }
    }
  );
}

